import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';

const LogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({
    level: '',
    category: '',
    limit: 500
  });
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const loadLogs = () => {
      const filteredLogs = logger.getLogs(filter);
      setLogs(filteredLogs);
    };

    loadLogs();

    if (autoRefresh) {
      const interval = setInterval(loadLogs, 2000); // Refresh every 2 seconds
      return () => clearInterval(interval);
    }
  }, [filter, autoRefresh]);

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARN': return 'text-yellow-400';
      case 'INFO': return 'text-blue-400';
      case 'DEBUG': return 'text-gray-400';
      default: return 'text-white';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Application Logs</h1>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              <span>Auto-refresh</span>
            </label>
            <button
              onClick={() => logger.clearLogs()}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded"
            >
              Clear Logs
            </button>
            <button
              onClick={() => logger.exportLogs()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
            >
              Export JSON
            </button>
          </div>
        </div>

        <div className="mb-4 flex gap-4">
          <select
            value={filter.level}
            onChange={(e) => setFilter({ ...filter, level: e.target.value })}
            className="bg-gray-800 text-white px-3 py-2 rounded"
          >
            <option value="">All Levels</option>
            <option value="ERROR">ERROR</option>
            <option value="WARN">WARN</option>
            <option value="INFO">INFO</option>
            <option value="DEBUG">DEBUG</option>
          </select>

          <select
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="bg-gray-800 text-white px-3 py-2 rounded"
          >
            <option value="">All Categories</option>
            <option value="VAULT">VAULT</option>
            <option value="BALANCE">BALANCE</option>
            <option value="TRADING">TRADING</option>
            <option value="AUTH">AUTH</option>
            <option value="WALLET">WALLET</option>
            <option value="API">API</option>
          </select>

          <select
            value={filter.limit}
            onChange={(e) => setFilter({ ...filter, limit: parseInt(e.target.value) })}
            className="bg-gray-800 text-white px-3 py-2 rounded"
          >
            <option value={100}>Last 100</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 overflow-auto max-h-[calc(100vh-250px)]">
          <div className="space-y-2 font-mono text-sm">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No logs found</div>
            ) : (
              logs.slice().reverse().map((log, index) => (
                <div
                  key={index}
                  className="border-b border-gray-700 pb-2 hover:bg-gray-700 px-2 py-1 rounded"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-gray-500 text-xs whitespace-nowrap">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <span className={`font-bold ${getLevelColor(log.level)}`}>
                      [{log.level}]
                    </span>
                    <span className="text-purple-400 font-semibold">
                      [{log.category}]
                    </span>
                    <span className="flex-1">{log.message}</span>
                  </div>
                  {log.data && (
                    <details className="mt-2 ml-20">
                      <summary className="cursor-pointer text-gray-400 text-xs">
                        View data
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-900 rounded text-xs overflow-auto">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-400">
          Showing {logs.length} log entries
        </div>
      </div>
    </div>
  );
};

export default LogsViewer;
